import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Container,
  Typography,
  Paper,
  Alert,
  Divider,
  Grid,
  CircularProgress,
  Fade,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  useTheme,
} from "@mui/material";
import {
  Save,
  ArrowBack,
  Security,
  Visibility,
  Edit,
  Delete,
  Group,
  Assessment,
  CheckCircle,
  Cancel,
  Person,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { useSelector } from "react-redux";
import configuration from "../config.js";

const PERMISSIONS = {
  read: {
    id: "read",
    label: "Read Access",
    description: "View and read content",
    icon: <Visibility />,
    colorKey: "info",
  },
  write: {
    id: "write",
    label: "Write Access",
    description: "Create and edit content",
    icon: <Edit />,
    colorKey: "warning",
  },
  delete: {
    id: "delete",
    label: "Delete Access",
    description: "Remove content and data",
    icon: <Delete />,
    colorKey: "error",
  },
  manage_users: {
    id: "manage_users",
    label: "Manage Users",
    description: "Create, edit, and manage user accounts",
    icon: <Group />,
    colorKey: "primary",
  },
  view_reports: {
    id: "view_reports",
    label: "View Reports",
    description: "Access analytics and reporting features",
    icon: <Assessment />,
    colorKey: "success",
  },
};

const useSubUserPermissions = () => {
  const [permissions, setPermissions] = useState([]);
  const [originalPermissions, setOriginalPermissions] = useState([]);
  const [subUserData, setSubUserData] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetchingUser, setFetchingUser] = useState(false);

  const clearMessages = useCallback(() => {
    setError("");
    setSuccess("");
  }, []);

  const hasChanges = useCallback(() => {
    return JSON.stringify(permissions.sort()) !== JSON.stringify(originalPermissions.sort());
  }, [permissions, originalPermissions]);

  return {
    permissions,
    setPermissions,
    originalPermissions,
    setOriginalPermissions,
    subUserData,
    setSubUserData,
    error,
    setError,
    success,
    setSuccess,
    loading,
    setLoading,
    fetchingUser,
    setFetchingUser,
    clearMessages,
    hasChanges,
  };
};

const useSubUserPermissionsAPI = (token) => {
  const { API_BASE_URL } = configuration;

  const fetchSubUser = useCallback(
    async (subUserId) => {
      if (!subUserId) {
        throw new Error("Sub-user ID is required");
      }

      const endpoint = `${API_BASE_URL}/api/v1/getSubUserById/${subUserId}`;
      const response = await axios.get(endpoint, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        withCredentials: true,
      });

      if (!response.data?.success) {
        throw new Error(response.data?.message || "Failed to fetch user data");
      }

      return response.data.data?.subUser || response.data.data;
    },
    [API_BASE_URL, token]
  );

  const updateSubUserPermissions = useCallback(
    async (subUserId, permissions) => {
      const endpoint = `${API_BASE_URL}/api/v1/updateSubUserById/${subUserId}/permissions`;
      const response = await axios.patch(
        endpoint,
        { permissions },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          withCredentials: true,
        }
      );

      if (!response.data?.success) {
        throw new Error(response.data?.message || "Update failed");
      }

      return response.data;
    },
    [API_BASE_URL, token]
  );

  return { fetchSubUser, updateSubUserPermissions };
};

// Loading Screen Component
const LoadingScreen = ({ subUserId }) => (
  <Container
    maxWidth="xs"
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100vh",
    }}
  >
    <Box sx={{ textAlign: "center" }}>
      <CircularProgress size={48} />
      <Typography variant="body1" sx={{ mt: 2, fontWeight: 500 }}>
        Loading permissions...
      </Typography>
      <Typography
        variant="caption"
        sx={{ mt: 1, display: "block", color: "text.secondary" }}
      >
        ID: {subUserId || "Not specified"}
      </Typography>
    </Box>
  </Container>
);

// Error Screen Component
const ErrorScreen = ({ error, subUserId, onGoBack }) => (
  <Container
    maxWidth="sm"
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "100vh",
      py: 2,
    }}
  >
    <Paper elevation={3} sx={{ p: 4, textAlign: "center", width: "100%" }}>
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
      <Typography variant="body2" sx={{ mb: 3 }}>
        <strong>Sub-User ID:</strong> {subUserId || "undefined"}
      </Typography>
      <Button
        variant="contained"
        startIcon={<ArrowBack />}
        onClick={onGoBack}
        sx={{ px: 4 }}
      >
        Go Back
      </Button>
    </Paper>
  </Container>
);

// User Info Card Component
const UserInfoCard = ({ subUserData }) => {
  const theme = useTheme();
  
  return (
    <Card
      elevation={2}
      sx={{ 
        mb: 3, 
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        color: theme.palette.primary.contrastText,
      }}
      component={motion.div}
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3 }}
    >
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Avatar sx={{ 
            bgcolor: "rgba(238, 232, 232, 0.2)", 
            width: 56, 
            height: 56,
            color: theme.palette.primary.contrastText,
          }}>
            <Person />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.primary.contrastText}}>
              {subUserData?.username || "Unknown User"}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9,color: theme.palette.primary.contrastText}}>
              {subUserData?.email || "No email provided"}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8,color: theme.palette.primary.contrastText }}>
              Prefix: {subUserData?.subUserPrefix || subUserData?.prefix || "N/A"}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

// Permission Card Component
const PermissionCard = ({ permission, isActive, onToggle, disabled }) => {
  const theme = useTheme();
  const permissionData = PERMISSIONS[permission.id];
  const color = theme.palette[permissionData.colorKey];
  
  return (
    <Card
      elevation={isActive ? 4 : 1}
      sx={{
        transition: "all 0.3s ease",
        transform: isActive ? "translateY(-2px)" : "none",
        borderLeft: isActive ? `4px solid ${color.main}` : `4px solid ${theme.palette.grey[300]}`,
        opacity: disabled ? 0.6 : 1,
        cursor: disabled ? "default" : "pointer",
        "&:hover": disabled ? {} : {
          transform: "translateY(-2px)",
          boxShadow: theme.shadows[3],
        },
      }}
      component={motion.div}
      whileHover={disabled ? {} : { scale: 1.02 }}
      whileTap={disabled ? {} : { scale: 0.98 }}
      onClick={disabled ? undefined : onToggle}
    >
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Avatar
            sx={{
              bgcolor: isActive ? color.main : theme.palette.grey[300],
              color: isActive ? color.contrastText || theme.palette.common.white : theme.palette.text.secondary,
              width: 48,
              height: 48,
            }}
          >
            {permissionData.icon}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
              {permissionData.label}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {permissionData.description}
            </Typography>
          </Box>
          <Switch
            checked={isActive}
            onChange={onToggle}
            disabled={disabled}
            color={permissionData.colorKey}
            size="medium"
          />
        </Box>
      </CardContent>
    </Card>
  );
};

// Permissions Summary Component
const PermissionsSummary = ({ permissions, originalPermissions }) => {
  const theme = useTheme();
  const activeCount = permissions.length;
  const totalCount = Object.keys(PERMISSIONS).length;
  const hasChanges = JSON.stringify(permissions.sort()) !== JSON.stringify(originalPermissions.sort());

  return (
    <Card elevation={2} sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Permissions Summary
          </Typography>
          {hasChanges && (
            <Chip
              icon={<Edit />}
              label="Changes Pending"
              color="warning"
              variant="outlined"
              size="small"
            />
          )}
        </Box>
        
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
          <Chip
            icon={<Security />}
            label={`${activeCount}/${totalCount} Permissions Active`}
            color={activeCount > 0 ? "success" : "default"}
            variant={activeCount > 0 ? "filled" : "outlined"}
          />
        </Box>

        <List dense>
          {permissions.map((permissionId) => {
            const permissionData = PERMISSIONS[permissionId];
            const color = theme.palette[permissionData.colorKey];
            return (
              <ListItem key={permissionId} sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <CheckCircle sx={{ color: color.main, fontSize: 20 }} />
                </ListItemIcon>
                <ListItemText primary={permissionData.label} />
              </ListItem>
            );
          })}
          {permissions.length === 0 && (
            <ListItem>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Cancel sx={{ color: theme.palette.grey[400], fontSize: 20 }} />
              </ListItemIcon>
              <ListItemText 
                primary="No permissions assigned" 
                sx={{ color: "text.secondary" }}
              />
            </ListItem>
          )}
        </List>
      </CardContent>
    </Card>
  );
};


const SubUserPermissionsPage = () => {
  const theme = useTheme();
  const {
    permissions,
    setPermissions,
    originalPermissions,
    setOriginalPermissions,
    subUserData,
    setSubUserData,
    error,
    setError,
    success,
    setSuccess,
    loading,
    setLoading,
    fetchingUser,
    setFetchingUser,
    clearMessages,
    hasChanges,
  } = useSubUserPermissions();

  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const { token } = useSelector((state) => state.auth);
  const { fetchSubUser, updateSubUserPermissions } = useSubUserPermissionsAPI(token);

  const subUserId = params.id || params.subUserId || params.userId;
  const passedSubUserData = location.state?.subUserData;

  
  const handleFetchSubUserData = useCallback(async () => {
    if (!subUserId) {
      setError("Sub-user ID is required");
      return;
    }

    setFetchingUser(true);
    clearMessages();

    try {
      const userData = await fetchSubUser(subUserId);
      setSubUserData(userData);
      const userPermissions = userData.permissions || [];
      setPermissions(userPermissions);
      setOriginalPermissions(userPermissions);
    } catch (err) {
      console.error("Error fetching sub-user data:", err);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to fetch user data. Please try again."
      );
    } finally {
      setFetchingUser(false);
    }
  }, [
    subUserId,
    fetchSubUser,
    setSubUserData,
    setPermissions,
    setOriginalPermissions,
    setError,
    setFetchingUser,
    clearMessages,
  ]);

  // Handle permission toggle
  const handlePermissionToggle = useCallback(
    (permissionId) => {
      setPermissions((prev) => {
        if (prev.includes(permissionId)) {
          return prev.filter((p) => p !== permissionId);
        } else {
          return [...prev, permissionId];
        }
      });
      clearMessages();
    },
    [setPermissions, clearMessages]
  );

  // Handle form submission
  const handleUpdatePermissions = useCallback(async () => {
    setLoading(true);
    clearMessages();

    try {
      await updateSubUserPermissions(subUserId, permissions);
      setSuccess("Permissions updated successfully!");
      setOriginalPermissions(permissions);
      
      // Refresh data
      await handleFetchSubUserData();
    } catch (err) {
      console.error("Permission update error:", err);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to update permissions. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }, [
    subUserId,
    permissions,
    updateSubUserPermissions,
    setLoading,
    setSuccess,
    setError,
    setOriginalPermissions,
    clearMessages,
    handleFetchSubUserData,
  ]);

  const handleGoBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleReset = useCallback(() => {
    setPermissions(originalPermissions);
    clearMessages();
  }, [originalPermissions, setPermissions, clearMessages]);

  useEffect(() => {
    if (!token) {
      setError("Authentication token not found. Please log in again.");
      return;
    }

    if (!configuration.API_BASE_URL) {
      setError("API configuration not found.");
      return;
    }

    if (passedSubUserData) {
      setSubUserData(passedSubUserData);
      const userPermissions = passedSubUserData.permissions || [];
      setPermissions(userPermissions);
      setOriginalPermissions(userPermissions);
      return;
    }

    if (subUserId) {
      handleFetchSubUserData();
    } else {
      setError("Sub-user ID is required.");
    }
  }, [
    subUserId,
    token,
    passedSubUserData,
    handleFetchSubUserData,
    setError,
    setSubUserData,
    setPermissions,
    setOriginalPermissions,
  ]);

  if (fetchingUser) {
    return <LoadingScreen subUserId={subUserId} />;
  }

  if (error && !passedSubUserData && !subUserData) {
    return (
      <ErrorScreen
        error={error}
        subUserId={subUserId}
        onGoBack={handleGoBack}
      />
    );
  }

  return (
    <Container
      maxWidth="lg"
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        minHeight: "100vh",
        py: 4,
      }}
    >
      <Paper
        elevation={8}
        sx={{
          p: { xs: 3, sm: 4 },
          borderRadius: 2,
          background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.secondary.light} 100%)`,
        }}
        component={motion.div}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        {/* Header */}
        <Box sx={{ mb: 4, textAlign: "center" }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 1,
              background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Sub-User Permissions
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            Manage access permissions for this sub-user
          </Typography>
        </Box>

        {/* Messages */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
            >
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {success && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
            >
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            </motion.div>
          )}
        </AnimatePresence>

        <Grid container spacing={3}>
          {/* Left Column - User Info and Summary */}
          <Grid item xs={12} md={4}>
            {subUserData && <UserInfoCard subUserData={subUserData} />}
            <PermissionsSummary 
              permissions={permissions} 
              originalPermissions={originalPermissions}
            />
          </Grid>

          
          <Grid item xs={12} md={8}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Available Permissions
            </Typography>
            
            <Grid container spacing={2}>
              {Object.values(PERMISSIONS).map((permission, index) => (
                <Grid item xs={12} key={permission.id}>
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                  >
                    <PermissionCard
                      permission={permission}
                      isActive={permissions.includes(permission.id)}
                      onToggle={() => handlePermissionToggle(permission.id)}
                      disabled={loading}
                    />
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>

       
        <Box
          sx={{
            mt: 4,
            pt: 3,
            borderTop: "1px solid",
            borderColor: "divider",
            display: "flex",
            gap: 2,
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleUpdatePermissions}
            disabled={loading || !hasChanges()}
            size="large"
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              backgroundColor: "success.main",
              "&:hover": {
                backgroundColor: "success.dark",
              },
              "&:disabled": {
                backgroundColor: "action.disabledBackground",
              },
            }}
          >
            {loading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Updating...
              </>
            ) : (
              "Save Permissions"
            )}
          </Button>

          <Button
            variant="outlined"
            onClick={handleReset}
            disabled={loading || !hasChanges()}
            size="large"
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Reset Changes
          </Button>

          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={handleGoBack}
            disabled={loading}
            size="large"
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Go Back
          </Button>
        </Box>

      
        <Box sx={{ mt: 4 }}>
          <Divider sx={{ mb: 3 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontWeight: 500 }}
            >
              Permission Management System
            </Typography>
          </Divider>
          <Typography
            variant="body2"
            sx={{ textAlign: "center" }}
            color="text.secondary"
          >
            Changes will take effect immediately after saving. Users may need to log out and log back in for changes to apply.
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default SubUserPermissionsPage;